import type {
  Blob2DInPatchUnits,
  OrientedBlob2DInPatchUnits,
} from './Blob2DInPatchUnits';

export type FitOrientedBlobsArgs = {
  blobs: ReadonlyArray<Blob2DInPatchUnits>;
  gridW: number;
};

function aabbFallback(blob: Blob2DInPatchUnits): OrientedBlob2DInPatchUnits {
  return {
    cx: blob.x + blob.width / 2,
    cy: blob.y + blob.height / 2,
    width: blob.width,
    height: blob.height,
    rotation: 0,
    score: blob.score,
    patchCount: blob.patchCount,
  };
}

function fitOne(
  blob: Blob2DInPatchUnits,
  gridW: number,
): OrientedBlob2DInPatchUnits {
  const indices = blob.patchIndices;

  // No patches recorded — fall back to the axis-aligned bbox as a 0-rotation
  // oriented blob.
  if (!indices || indices.length === 0) {
    return aabbFallback(blob);
  }

  const n = indices.length;

  // Use patch-center coordinates (col + 0.5, row + 0.5) so a single-patch
  // blob has a well-defined center at the middle of that patch.
  let sumX = 0;
  let sumY = 0;

  for (let i = 0; i < n; i += 1) {
    const idx = indices[i];
    const r = Math.floor(idx / gridW);
    const c = idx - r * gridW;
    sumX += c + 0.5;
    sumY += r + 0.5;
  }

  const meanX = sumX / n;
  const meanY = sumY / n;

  let sxx = 0;
  let syy = 0;
  let sxy = 0;

  for (let i = 0; i < n; i += 1) {
    const idx = indices[i];
    const r = Math.floor(idx / gridW);
    const c = idx - r * gridW;
    const dx = c + 0.5 - meanX;
    const dy = r + 0.5 - meanY;
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  }

  // Symmetric 2x2 eigen-decomposition. λ = trace/2 ± sqrt((sxx-syy)²/4 + sxy²).
  const half = (sxx + syy) / 2;
  const diff = (sxx - syy) / 2;
  const disc = Math.sqrt(diff * diff + sxy * sxy);
  const lambdaMax = half + disc;

  // Principal eigenvector for lambdaMax. When disc ≈ 0, every direction is an
  // eigenvector — fall back to axis-aligned.
  if (disc < 1e-9) {
    return aabbFallback(blob);
  }

  // For the symmetric 2x2 matrix [[sxx, sxy], [sxy, syy]], a stable
  // eigenvector for λ_max is (sxy, λ_max - sxx) when sxy is non-trivial,
  // else (1, 0) if sxx >= syy, else (0, 1).
  let vx: number;
  let vy: number;

  if (Math.abs(sxy) > 1e-9) {
    vx = lambdaMax - syy;
    vy = sxy;
  } else if (sxx >= syy) {
    vx = 1;
    vy = 0;
  } else {
    vx = 0;
    vy = 1;
  }

  const vNorm = Math.hypot(vx, vy);
  vx /= vNorm;
  vy /= vNorm;

  // Perpendicular direction (rotated +90° CCW from v).
  const wx = -vy;
  const wy = vx;

  // Project every patch center onto (v, w) and track min/max along each.
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;

  for (let i = 0; i < n; i += 1) {
    const idx = indices[i];
    const r = Math.floor(idx / gridW);
    const c = idx - r * gridW;
    const dx = c + 0.5 - meanX;
    const dy = r + 0.5 - meanY;
    const u = dx * vx + dy * vy;
    const vCoord = dx * wx + dy * wy;
    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
    if (vCoord < minV) minV = vCoord;
    if (vCoord > maxV) maxV = vCoord;
  }

  // Inflate by one patch (0.5 on each side) so the box covers the full extent
  // of each unit-square patch, not just the patch centers.
  const halfWidth = (maxU - minU) / 2 + 0.5;
  const halfHeight = (maxV - minV) / 2 + 0.5;

  // The mean-centered min/max may not be symmetric — shift the center along
  // the principal axes so the box stays tight around the data.
  const uCenter = (maxU + minU) / 2;
  const vCenter = (maxV + minV) / 2;
  const cx = meanX + uCenter * vx + vCenter * wx;
  const cy = meanY + uCenter * vy + vCenter * wy;

  // Canonicalize: keep rotation in [-π/2, π/2] by flipping the longer side
  // onto the local x-axis if needed. Avoids rotation jitter at the boundary.
  let width = 2 * halfWidth;
  let height = 2 * halfHeight;
  let rotation = Math.atan2(vy, vx);

  if (rotation > Math.PI / 2) {
    rotation -= Math.PI;
  } else if (rotation < -Math.PI / 2) {
    rotation += Math.PI;
  }

  if (height > width) {
    [width, height] = [height, width];
    rotation += rotation > 0 ? -Math.PI / 2 : Math.PI / 2;
  }

  return {
    cx,
    cy,
    width,
    height,
    rotation,
    score: blob.score,
    patchCount: blob.patchCount,
  };
}

// Fit an oriented bounding box to each blob's patch set via PCA on the patch
// centers.
//
// The first principal axis of the centered patch coordinates becomes the
// local x-axis of the box; the perpendicular direction is the local y-axis.
// Box extents are the projected range of patches onto each axis, inflated by
// one patch unit so the box encloses the full footprint of every patch
// (centers + half-patch margin on either side).
//
// Falls back to the input's axis-aligned bbox (rotation 0) when:
// - The blob has no patch indices recorded (caller forgot `collectPatches`).
// - The covariance matrix is degenerate (single patch or all collinear with
//   indistinguishable principal direction).
//
// Returns a parallel array — output[i] corresponds to blobs[i].
export function fitOrientedBlobs(
  args: FitOrientedBlobsArgs,
): OrientedBlob2DInPatchUnits[] {
  const { blobs, gridW } = args;
  const out: OrientedBlob2DInPatchUnits[] = [];

  for (const blob of blobs) {
    out.push(fitOne(blob, gridW));
  }

  return out;
}
